/**
 * #855 item 3 — Admin Reporting date-range quick picks (@dylan219: "select
 * today, yesterday, this week, last week, this month, last month as options
 * when selecting the date range ... calendar but also those options").
 *
 * Each preset resolves to a HALF-OPEN window `[from, to)` expressed as the
 * Reporting screen's `datetime-local` input values (`YYYY-MM-DDTHH:mm`), which
 * the screen treats as UTC wall-clock times (UTC is the agreed reporting
 * timezone, #668). Boundaries are therefore UTC midnights:
 *
 *   - Today       — today 00:00 → tomorrow 00:00
 *   - Yesterday   — yesterday 00:00 → today 00:00
 *   - This week   — this Monday 00:00 → next Monday 00:00
 *   - Last week   — last Monday 00:00 → this Monday 00:00
 *   - This month  — the 1st 00:00 → the 1st of next month 00:00
 *   - Last month  — the 1st of last month 00:00 → the 1st 00:00
 *
 * A week runs Monday–Sunday (the Irish/ISO-8601 convention). Every window is
 * at most 31 days, inside the BFF's 31-day report cap. Computed from `now` at
 * call time — nothing is hard-coded.
 */
export type ReportDatePreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth';

/** The quick-pick options, in display order. */
export const REPORT_DATE_PRESETS: ReadonlyArray<{
  readonly value: ReportDatePreset;
  readonly label: string;
}> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This week' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** `Date` → the `datetime-local` value (UTC wall clock, minute precision). */
function toInput(d: Date): string {
  return d.toISOString().slice(0, 16);
}

/** UTC midnight of `d`'s calendar day. */
function utcMidnight(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
}

/**
 * Resolve a preset to `{ from, to }` `datetime-local` values (UTC), relative
 * to `now`.
 */
export function reportPresetWindow(
  preset: ReportDatePreset,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = utcMidnight(now);
  // getUTCDay(): Sunday = 0 … Saturday = 6 → days since Monday (Mon = 0).
  const sinceMonday = (today.getUTCDay() + 6) % 7;
  const monday = new Date(today.getTime() - sinceMonday * DAY_MS);
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  let from: Date;
  let to: Date;
  switch (preset) {
    case 'today':
      from = today;
      to = new Date(today.getTime() + DAY_MS);
      break;
    case 'yesterday':
      from = new Date(today.getTime() - DAY_MS);
      to = today;
      break;
    case 'thisWeek':
      from = monday;
      to = new Date(monday.getTime() + 7 * DAY_MS);
      break;
    case 'lastWeek':
      from = new Date(monday.getTime() - 7 * DAY_MS);
      to = monday;
      break;
    case 'thisMonth':
      from = new Date(Date.UTC(y, m, 1));
      to = new Date(Date.UTC(y, m + 1, 1));
      break;
    case 'lastMonth':
      from = new Date(Date.UTC(y, m - 1, 1));
      to = new Date(Date.UTC(y, m, 1));
      break;
  }
  return { from: toInput(from), to: toInput(to) };
}
